import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Skeleton,
} from '@mui/material';
import {
  TrendingUp,
  Radar,
  Refresh as RefreshIcon,
  SearchOff as SearchOffIcon,
  LightbulbOutlined as LightbulbIcon,
  CalendarMonth as CalendarIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import { scannerAPI } from '../services/api';
import { StatsCards, BuyReadyTable, WatchlistTable, TechnicalMetricsTable } from '../components/early-pivot';

const EarlyPivotScanner = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [saveToDb, setSaveToDb] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const didAutoScan = useRef(false);

  const runScan = useCallback(async () => {
    setLoading(true);
    setSnackbar({ open: true, message: t('earlyPivot.scanningMessage') });
    try {
      const res = await scannerAPI.scanEarlyPivot({ save_to_db: saveToDb });
      setResults(res.data);
      setSnackbar({
        open: true,
        message: t('earlyPivot.scanDone', {
          scanned: res.data?.total_scanned || 0,
          signals: res.data?.total_signals || 0,
        }),
        severity: 'success',
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || t('earlyPivot.apiError');
      setSnackbar({ open: true, message: msg, severity: 'error' });
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [saveToDb, t]);

  useEffect(() => {
    if (!didAutoScan.current) {
      didAutoScan.current = true;
      runScan();
    }
  }, [runScan]);

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
      {/* ═══════ HEADER ═══════ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2,
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.25)',
          }}>
            <TrendingUp sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('earlyPivot.title')}
              </Typography>
              {results?.scan_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', boxShadow: '0 0 6px #10b981' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {results.scan_date.split(' ')[0]}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('earlyPivot.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={<Checkbox checked={saveToDb} onChange={(e) => setSaveToDb(e.target.checked)} size="small" sx={{ p: 0.5, '&.Mui-checked': { color: 'primary.main' } }} />}
            label={t('earlyPivot.saveToDb')}
            sx={{
              border: '1px solid', borderColor: saveToDb ? 'primary.main' : '#e2e8f0',
              borderRadius: 1, px: 1, py: 0, m: 0, height: 34,
              bgcolor: saveToDb ? 'rgba(33,150,243,0.04)' : 'transparent',
              '& .MuiFormControlLabel-label': { fontSize: '0.75rem', fontWeight: 600, color: saveToDb ? 'primary.main' : '#64748b' },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Radar sx={{ fontSize: 16 }} />}
            onClick={runScan}
            disabled={loading}
            sx={{ fontSize: '0.75rem', fontWeight: 600, minHeight: 34, textTransform: 'none' }}
          >
            {loading ? t('earlyPivot.scanning') : t('earlyPivot.scan')}
          </Button>
        </Box>
      </Box>

      {/* Loading Skeleton */}
      {loading ? (
        <Box sx={{ mt: 3 }}>
          {/* Stats Cards Skeleton */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ flex: '1 1 0', minWidth: 200, p: 2.5, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: 'background.paper' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Skeleton variant="circular" width={18} height={18} />
                  <Skeleton variant="text" width={80} height={14} />
                </Box>
                <Skeleton variant="text" width={60} height={32} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={12} />
              </Box>
            ))}
          </Box>

          {/* Alert Skeleton */}
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 3 }} />

          {/* Main Table Skeleton */}
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, mb: 3, overflow: 'hidden' }}>
            {/* Table Header */}
            <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Skeleton variant="text" width={200} height={24} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={350} height={14} />
            </Box>
            {/* Table Rows */}
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Skeleton variant="text" width={30} height={20} />
                  <Skeleton variant="text" width={60} height={20} />
                  <Skeleton variant="text" width={70} height={20} />
                  <Skeleton variant="text" width={80} height={20} />
                  <Skeleton variant="text" width={120} height={20} />
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width={80} height={20} />
                  <Skeleton variant="text" width={70} height={20} />
                  <Skeleton variant="text" width={60} height={20} />
                </Box>
              ))}
            </Box>
          </Box>

          {/* Technical Metrics Table Skeleton */}
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="text" width={120} height={14} />
              </Box>
              <Skeleton variant="text" width="60%" height={12} />
            </Box>
            <Box sx={{ p: 2, overflowX: 'auto' }}>
              <Box sx={{ minWidth: 900 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                    <Skeleton variant="text" width={40} height={16} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="text" width={70} height={16} sx={{ flexShrink: 0 }} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                    <Skeleton variant="text" sx={{ flex: 1 }} height={16} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}

      {/* No Results — Professional Empty State */}
      {results != null && results.total_signals === 0 && !loading && (
        <Box sx={{
          mt: 2,
          p: { xs: 4, md: 6 },
          borderRadius: 3,
          textAlign: 'center',
          border: '1.5px solid',
          borderColor: 'rgba(16, 185, 129, 0.15)',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, #ffffff 100%)',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.08)',
        }}>
          {/* Icon */}
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2.5,
          }}>
            <SearchOffIcon sx={{ fontSize: 34, color: '#059669' }} />
          </Box>

          {/* Title */}
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', mb: 1 }}>
            {t('earlyPivot.empty.title')}
          </Typography>

          {/* Scanned count */}
          {results.total_scanned > 0 && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mb: 1.5, fontWeight: 500 }}>
              {t('earlyPivot.empty.scannedCount', { count: results.total_scanned })}
            </Typography>
          )}

          {/* Description */}
          <Typography sx={{ fontSize: '0.88rem', color: '#64748b', maxWidth: 520, mx: 'auto', mb: 3.5, lineHeight: 1.7 }}>
            {t('earlyPivot.empty.description')}
          </Typography>

          {/* Suggestion hints */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 460, mx: 'auto', textAlign: 'left', mb: 3.5 }}>
            {[
              { icon: <ShowChartIcon sx={{ fontSize: 18 }} />, text: t('earlyPivot.empty.hint1') },
              { icon: <CalendarIcon sx={{ fontSize: 18 }} />, text: t('earlyPivot.empty.hint2') },
              { icon: <LightbulbIcon sx={{ fontSize: 18 }} />, text: t('earlyPivot.empty.hint3') },
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: 1.5,
                  bgcolor: 'rgba(16, 185, 129, 0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#059669', flexShrink: 0,
                }}>
                  {item.icon}
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500, lineHeight: 1.4 }}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Rescan button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={runScan}
            disabled={loading}
            sx={{
              fontSize: '0.78rem', fontWeight: 600, textTransform: 'none',
              borderColor: '#10b981', color: '#059669',
              '&:hover': { borderColor: '#059669', bgcolor: 'rgba(16, 185, 129, 0.06)' },
            }}
          >
            {t('earlyPivot.empty.rescan')}
          </Button>
        </Box>
      )}

      {/* Results */}
      {results?.results?.length > 0 && (
        <>
          {/* Stats Cards */}
          <StatsCards results={results} />

          {/* Buy Ready Section */}
          <BuyReadyTable results={results} />

          {/* Watchlist Section */}
          <WatchlistTable results={results} />

          {/* Technical Metrics Table */}
          <TechnicalMetricsTable results={results} />
        </>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ minWidth: 300 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EarlyPivotScanner;
